import { logger } from "@/utils/logger";
import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDealerSession } from '@/contexts/DealerSessionContext';
import { useLanguage } from '@/contexts/LanguageContext';

interface WaitingVideoData {
  Waiting_Youtube_Video_url: string;
  Waiting_Message: string;
}

export const useWaitingVideo = () => {
  const [waitingVideoData, setWaitingVideoData] = useState<WaitingVideoData | null>(null);
  const [isLoadingWaitingVideo, setIsLoadingWaitingVideo] = useState(false);
  const { sessionData, isLoading: isSessionLoading } = useDealerSession();
  const { language } = useLanguage();
  const hasCalledRef = useRef(false);
  const sessionDataRef = useRef(sessionData);

  // Keep a ref to the latest sessionData so async waits can see updated values
  useEffect(() => {
    sessionDataRef.current = sessionData;
  }, [sessionData]);

  const fetchWaitingVideo = useCallback(async (userQuestion: string): Promise<void> => {
    const initialSessionData = sessionDataRef.current;

    logger.log('🎥 fetchWaitingVideo called', {
      hasSessionData: !!initialSessionData,
      isSessionLoading,
      waitingVideoFlag: initialSessionData?.waitingVideo,
      hasCalledBefore: hasCalledRef.current,
      dealerId: initialSessionData?.dealerId,
    });

    // If session is still loading, wait a bit for it to finish
    if (isSessionLoading) {
      logger.log('🎥 Session still loading, waiting 500ms...');
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Re-check after waiting (IMPORTANT: use ref so we get the latest)
    const latestSessionData = sessionDataRef.current;

    if (!latestSessionData) {
      logger.log('🎥 No session data available after waiting');
      return;
    }

    // Only fetch if waiting_video is enabled and we haven't called before
    if (!latestSessionData.waitingVideo || latestSessionData.videosEnabled === false || hasCalledRef.current) {
      logger.log('🎥 Skipping waiting video fetch:', {
        waitingVideoDisabled: !latestSessionData.waitingVideo,
        videosDisabled: latestSessionData.videosEnabled === false,
        alreadyCalled: hasCalledRef.current,
        sessionDataExists: !!latestSessionData,
      });
      return;
    }

    logger.log('🎥 Fetching waiting video for dealer:', latestSessionData.dealerId);
    setIsLoadingWaitingVideo(true);
    hasCalledRef.current = true; // Mark as called AFTER validation passes

    try {
      const { data, error } = await supabase.functions.invoke('waiting-video', {
        body: {
          userQuestion,
          dealerId: latestSessionData.dealerId,
          language: language,
        },
      });

      // RAW RESPONSE LOGGING
      logger.log('🎬 RAW WAITING VIDEO RESPONSE:', JSON.stringify({ data, error }, null, 2));

      if (error) {
        console.error('❌ Failed to fetch waiting video:', error);
        return;
      }

      // Set data if we have a message (URL can be empty for "Close" response)
      if (data?.Waiting_Message) {
        setWaitingVideoData(data);
        if (data.Waiting_Message === 'Close') {
          logger.log('🚪 Received "Close" signal from waiting video');
        } else {
          logger.log('✅ Waiting video fetched:', data.Waiting_Youtube_Video_url);
        }
      } else {
        logger.log('⚠️ No waiting video data in response:', data);
      }
    } catch (error) {
      console.error('❌ Error fetching waiting video:', error);
    } finally {
      setIsLoadingWaitingVideo(false);
    }
  }, [isSessionLoading, language]);

  const clearWaitingVideo = useCallback(() => {
    setWaitingVideoData(null);
  }, []);

  const resetWaitingVideoFlag = useCallback(() => {
    hasCalledRef.current = false;
  }, []);

  return {
    waitingVideoData,
    isLoadingWaitingVideo,
    fetchWaitingVideo,
    clearWaitingVideo,
    resetWaitingVideoFlag
  };
};